/* eslint-disable */
// Quantity cell of the multi-order table (order / buyerOrder modes).
// - A count item shows its sub-unit ("pax") instead of "pc".
// - With "Enter by weight" on, a count item with a min weight is typed in kg and stored as a
//   count: kg * 1000 / minWeight. Only multiples of the min weight are allowed - anything else
//   is flagged while typing and snapped to the nearest multiple when the box is left. The count
//   is what gets saved.
import { useState } from 'react';
import { useMultiplePo } from '../contexts/MultiplePoContext';
import {
	countToKg, countUnitLabel, isCountItem, kgToCount,
} from '../utils/countUnits';

const blockKeys = (e) => ['-', '+', "'", 'e', 'E'].includes(e.key) && e.preventDefault();

const inputClass = 'text-center w-[50px] border border-gray-300 focus:border-blue-500 outline-none transition-colors duration-200';
const inputStyle = { borderRadius: '4px', padding: '2px 6px' };

const StoreQtyInput = ({ value, unit, onChange, viewMode, rowData, header }) => {
	const { qtyByWeight, productInfoById } = useMultiplePo();
	const info = productInfoById?.[rowData?.[header?.id]?.itemId] ?? null;
	const isCount = isCountItem(info);
	const label = isCount ? countUnitLabel(info, unit) : unit;
	const byWeight = qtyByWeight && isCount && !!info?.minWeight;

	// While typing in kg, keep the text as typed; otherwise show the count converted back.
	const [kgText, setKgText] = useState(null);

	if (!byWeight) {
		// Ordering by count: the weight it comes to, when the variant has a min weight.
		const weightHint = isCount && info?.minWeight && Number(value) > 0
			? countToKg(value, info.minWeight)
			: null;
		return (
			<div className='flex flex-col items-start'>
				<div className='flex items-center gap-1'>
					<input
						type='number'
						className={inputClass}
						style={inputStyle}
						value={value ?? ''}
						disabled={viewMode}
						onChange={(e) => onChange(e.target.value)}
						onKeyDown={blockKeys}
					/>
					<span className='text-[11px] text-gray-500'>{label}</span>
				</div>
				{weightHint !== null && (
					<span className='text-[10px] text-gray-400'>= {weightHint} kg</span>
				)}
			</div>
		);
	}

	const shownKg = kgText ?? (value !== '' && value !== null && value !== undefined
		? String(countToKg(value, info.minWeight) ?? '')
		: '');
	// Typed weight that is not a whole number of units (beyond float noise).
	const typedUnits = kgText !== null && kgText !== '' ? (Number(kgText) * 1000) / info.minWeight : null;
	const notMultiple = typedUnits !== null && !Number.isNaN(typedUnits)
		&& Math.abs(typedUnits - Math.round(typedUnits)) > 1e-6;
	const stepKg = info.minWeight / 1000;

	return (
		<div className='flex flex-col items-start'>
			<div className='flex items-center gap-1'>
				<input
					type='number'
					step={stepKg}
					min={0}
					className={`${inputClass} ${notMultiple ? '!border-red-500 bg-red-50' : ''}`}
					style={inputStyle}
					title={`Multiples of ${info.minWeight} g only`}
					value={shownKg}
					disabled={viewMode}
					onFocus={() => setKgText(shownKg)}
					onBlur={() => setKgText(null)}
					onChange={(e) => {
						const text = e.target.value;
						setKgText(text);
						if (text === '') {
							onChange('');
							return;
						}
						const count = kgToCount(text, info.minWeight);
						if (count !== null) onChange(String(count));
					}}
					onKeyDown={blockKeys}
				/>
				<span className='text-[11px] text-gray-500'>kg</span>
			</div>
			{notMultiple ? (
				<span className='text-[10px] text-red-600'>
					Multiples of {info.minWeight} g only - will be {countToKg(value, info.minWeight)} kg
				</span>
			) : (value !== '' && value !== null && value !== undefined && Number(value) > 0 && (
				<span className='text-[10px] text-gray-400'>
					= {value} {countUnitLabel(info)}
				</span>
			))}
		</div>
	);
};

export default StoreQtyInput;
